import {
  faBold,
  faH1,
  faH2,
  faH3,
  faItalic,
  faLink,
  faList,
  faStrikethrough,
  faUnlink,
} from "@fortawesome/pro-regular-svg-icons";
import {
  faBold as faBoldSolid,
  faCaretDown,
  faCaretUp,
  faItalic as faItalicSolid,
  faStrikethrough as faStrikethroughSolid,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Editor } from "@tiptap/core";
import { BubbleMenu } from "@tiptap/react";
import { findParentNodeOfType } from "prosemirror-utils";
import { useCallback } from "react";

import { useCeramic } from "@/hooks";
import { Page } from "@/types";

import styles from "./MenuBar.module.scss";

export const MenuBar = ({ page, editor }: { page: Page; editor?: Editor | null }) => {
  if (!editor) {
    return null;
  }

  const { address } = useCeramic();

  const vote = async (value = 1) => {
    const node = findParentNodeOfType(editor?.schema.nodes.listItem)(editor?.view.state.selection);
    const listItem = node?.node;
    const blockId = listItem?.attrs.blockId;
    let votes: { address: string; value: number }[] = [];
    if (!listItem?.attrs.votes || !Array.isArray(listItem?.attrs.votes)) {
      votes = [];
    } else {
      votes = [...listItem?.attrs.votes];
    }
    if (address) votes.push({ address, value });
    editor?.state.doc.descendants((node, pos) => {
      if (node.attrs.blockId && node.attrs.blockId === blockId) {
        const transaction = editor?.state.tr.setNodeAttribute(pos, "votes", votes);
        if (transaction.docChanged) {
          transaction.setMeta("addToHistory", false);
          editor.view.dispatch(transaction);
        }
      }
    });
  };

  const setLink = useCallback(() => {
    const previousUrl = editor.getAttributes("link").href;
    const url = window.prompt("URL", previousUrl);

    // cancelled
    if (url === null) {
      return;
    }

    // empty
    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();

      return;
    }

    // update link
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  }, [editor]);

  return (
    <BubbleMenu
      className={styles.bubble}
      editor={editor}
      tippyOptions={{
        duration: 100,
        theme: "light-border",
        maxWidth: 1000,
      }}
    >
      <button className={styles.vote} onClick={async () => vote()}>
        <FontAwesomeIcon icon={faCaretUp} /> Upvote
      </button>
      <button className={styles.vote} onClick={async () => vote(-1)}>
        <FontAwesomeIcon icon={faCaretDown} /> Downvote
      </button>
      <div className={styles.divider}></div>
      <button
        onClick={() => editor.chain().focus().toggleBold().run()}
        className={editor.isActive("bold") ? "is-active" : ""}
      >
        <FontAwesomeIcon icon={editor.isActive("bold") ? faBoldSolid : faBold} size="sm" />
      </button>
      <button
        onClick={() => editor.chain().focus().toggleItalic().run()}
        className={editor.isActive("italic") ? "is-active" : ""}
      >
        <FontAwesomeIcon icon={editor.isActive("italic") ? faItalicSolid : faItalic} size="sm" />
      </button>
      <button
        onClick={() => editor.chain().focus().toggleStrike().run()}
        className={editor.isActive("strike") ? "is-active" : ""}
      >
        <FontAwesomeIcon
          icon={editor.isActive("strike") ? faStrikethroughSolid : faStrikethrough}
          size="sm"
        />
      </button>
      <div className={styles.divider}></div>

      <button onClick={setLink} className={editor.isActive("link") ? "is-active" : ""}>
        <FontAwesomeIcon icon={faLink} size="sm" />
      </button>
      <button
        onClick={() => editor.chain().focus().unsetLink().run()}
        disabled={!editor.isActive("link")}
      >
        <FontAwesomeIcon icon={faUnlink} size="sm" />
      </button>
      <div className={styles.divider}></div>
      <button onClick={() => editor?.chain().focus().toggleHeading({ level: 1 }).run()}>
        <FontAwesomeIcon icon={faH1} size="sm" />
      </button>
      <button onClick={() => editor?.chain().focus().toggleHeading({ level: 2 }).run()}>
        <FontAwesomeIcon icon={faH2} size="sm" />
      </button>
      <button onClick={() => editor?.chain().focus().toggleHeading({ level: 3 }).run()}>
        <FontAwesomeIcon icon={faH3} size="sm" />
      </button>
      <button onClick={() => editor?.chain().focus().toggleBulletList().run()}>
        <FontAwesomeIcon icon={faList} size="sm" />
      </button>
    </BubbleMenu>
  );
};
